// ─── Парсер vCard (.vcf) ─────────────────────────────────────────────────────
// Поддержка vCard 3.0/4.0 (macOS Contacts, iCloud) и 2.1 (Android, quoted-printable).
// Возвращает массив контактов: { name, phone, email, birthday, notes, photo }
// photo — data URL (base64) из свойства PHOTO, либо ''

function decodeQuotedPrintable(str) {
  return str
    .replace(/=\r?\n/g, '') // soft line breaks
    .replace(/(?:=[0-9A-F]{2})+/gi, (m) => {
      const bytes = m.split('=').filter(Boolean).map(h => parseInt(h, 16));
      try {
        return new TextDecoder('utf-8').decode(new Uint8Array(bytes));
      } catch {
        return m;
      }
    });
}

function normalizeBday(value) {
  // YYYY-MM-DD | YYYYMMDD | --MMDD (без года — пропускаем)
  const v = value.trim();
  let m = v.match(/^(\d{4})-?(\d{2})-?(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return '';
}

export function parseVCard(text) {
  // Unfold: строки-продолжения начинаются с пробела/таба
  const unfolded = text.replace(/\r?\n[ \t]/g, '');
  const blocks = unfolded.split(/BEGIN:VCARD/i).slice(1);
  const contacts = [];

  for (const block of blocks) {
    const contact = { name: '', phone: '', email: '', birthday: '', notes: '', photo: '' };
    let nFallback = '';

    for (const rawLine of block.split(/\r?\n/)) {
      const line = rawLine.trim();
      if (!line || /^END:VCARD/i.test(line)) continue;
      const colonIdx = line.indexOf(':');
      if (colonIdx < 0) continue;

      const propPart = line.slice(0, colonIdx);
      let value = line.slice(colonIdx + 1);
      const propName = propPart.split(';')[0].split('.').pop().toUpperCase();

      if (/ENCODING=QUOTED-PRINTABLE/i.test(propPart)) {
        value = decodeQuotedPrintable(value);
      }

      switch (propName) {
        case 'FN':
          if (!contact.name) contact.name = value.trim();
          break;
        case 'N': {
          // family;given;middle;prefix;suffix → "given family"
          const parts = value.split(';');
          nFallback = [parts[1], parts[0]].filter(Boolean).join(' ').trim();
          break;
        }
        case 'TEL':
          if (!contact.phone) contact.phone = value.replace(/[^\d+\-() ]/g, '').trim();
          break;
        case 'EMAIL':
          if (!contact.email) contact.email = value.trim();
          break;
        case 'BDAY':
          if (!contact.birthday) contact.birthday = normalizeBday(value);
          break;
        case 'PHOTO': {
          if (contact.photo) break;
          if (/^data:image\//i.test(value)) {
            // vCard 4.0: PHOTO:data:image/jpeg;base64,...
            contact.photo = value;
          } else if (/ENCODING=(B\b|BASE64)/i.test(propPart) || /;(JPEG|PNG|GIF)/i.test(propPart)) {
            // vCard 2.1/3.0: PHOTO;ENCODING=b;TYPE=JPEG:<base64>
            const typeMatch = propPart.match(/TYPE=([A-Za-z]+)/i) || propPart.match(/;(JPEG|PNG|GIF)/i);
            const mime = 'image/' + (typeMatch ? typeMatch[1].toLowerCase() : 'jpeg');
            contact.photo = `data:${mime};base64,${value.replace(/\s+/g, '')}`;
          }
          // http(s)-ссылки на фото пропускаем
          break;
        }
        default:
          break;
      }
    }

    if (!contact.name) contact.name = nFallback;
    if (contact.name || contact.phone || contact.email) contacts.push(contact);
  }

  return contacts;
}

// data URL (base64) → Blob — для фото из vCard
export function dataUrlToBlob(dataUrl) {
  const commaIdx = dataUrl.indexOf(',');
  const head = dataUrl.slice(0, commaIdx);
  const b64 = dataUrl.slice(commaIdx + 1);
  const mime = head.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}
